import { unwrap } from '@hive/api';
import {
  AppShell,
  Brand,
  ContextPanel,
  ErrorBoundary,
  ExportProvider,
  HelpButton,
  Loading,
  Icon,
  LangFlags,
  matchCommands,
  MenuButton,
  Modal,
  nextTheme,
  PrefScopeProvider,
  ShellSlotsProvider,
  StatusBar,
  themeIcon,
  useCommands,
  useConfirm,
  useFocusTrap,
  useShellSlots,
  useSlotTarget,
  useToast,
  type ExportContextValue,
  type IconName,
  type ShellCommand,
  useLoad,
} from '@hive/ui';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useI18nP, useT } from '../i18n';
import { applyUpdate, consumeUpdatedFlag, useInstallPrompt, useUpdateReady } from '../lib/pwa';
import { searchRoute, type SearchHit } from '../lib/routes';
import { iconSrc, logoDataUri, logoSrc } from '../lib/logo';
import { RailBrandMark } from './RailBrand';
import { applyNavStyle, navStyleOf, themeOf, useSession } from '../lib/session';
import { autonomousOf, useScreenCatalog, useScreenCatalogState } from '../lib/screens';
import { AutonomousManaged } from './AutonomousManaged';
import { StepUpDialog } from './StepUpDialog';
import { InsightsButton } from '../screens/insights/InsightsButton';
import { BrakeModal } from '../screens/ops/BrakeModal';
import { AskDrawer } from './AskDrawer';
import { StartGuideButton } from './StartGuide';
import { canBrake, opsChanged, useOps } from '../screens/ops/ops';
import { profileName } from '../lib/wizards';
import { loadRelay } from '../lib/relay';
import { pushRecent } from '../lib/recent';

const SUB_KEY = 'hive.subnav.collapsed';
const GROUPS_KEY = 'hive.nav.groups';

interface Space {
  id: string;
  label: string;
  icon: IconName;
  items: NavItem[];
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Uma permissão, ou várias (basta ter uma). */
  perm?: string | string[];
  end?: boolean;
  badge?: number;
}

/** Largura de computador: trilho e painel de contexto fixos (abaixo disso, gaveta e barra de baixo). */
const WIDE = '(min-width: 961px)';
function useWide(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = typeof window.matchMedia === 'function' ? window.matchMedia(WIDE) : null;
      m?.addEventListener?.('change', cb);
      return () => m?.removeEventListener?.('change', cb);
    },
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(WIDE).matches : true),
    () => true,
  );
}

/**
 * Casca da plataforma (layout A + C): trilho escuro dos módulos, painel de contexto do módulo (recolhível), cabeçalho
 * com o caminho, a busca/comando e as ações da tela, a área de trabalho e o rodapé de status. As telas alimentam o
 * painel, o cabeçalho, o rodapé e os comandos pelos encaixes de @hive/ui (shellSlots.tsx).
 */
export function Shell({ children }: { children: ReactNode }) {
  const s = useSession();
  const branding = s.me.tenant?.branding ?? {};
  // logotipo completo embutido (data URI) no cabeçalho dos arquivos exportados, que abrem sem rede
  const logoUrl = logoSrc(s.tenant.slug, branding.logoUrl);
  const [logoData, setLogoData] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setLogoData(null);
    void logoDataUri(logoUrl).then((v) => {
      if (live) setLogoData(v);
    });
    return () => {
      live = false;
    };
  }, [logoUrl]);
  // menu Exportar: marca, quem exporta e o registro na trilha de auditoria
  const exportCtx = useMemo<ExportContextValue>(
    () => ({
      product: typeof branding.productName === 'string' && branding.productName ? branding.productName : 'Hive',
      brandColor: typeof branding.primaryColor === 'string' ? branding.primaryColor : null,
      logo: logoData,
      who: s.me.name || s.me.email || '',
      audit: (r) => unwrap(s.api.POST('/api/v1/me/exports' as never, { body: r } as never)),
    }),
    [branding.productName, branding.primaryColor, logoData, s.me.name, s.me.email, s.api],
  );
  // escolhas de tela (visão, camadas do mapa) lembradas por pessoa
  return (
    <PrefScopeProvider scope={s.me.id ?? s.me.email}>
      <ExportProvider value={exportCtx}>
        <ShellSlotsProvider>
          <ShellBody>{children}</ShellBody>
        </ShellSlotsProvider>
      </ExportProvider>
    </PrefScopeProvider>
  );
}

function ShellBody({ children }: { children: ReactNode }) {
  const t = useT();
  const s = useSession();
  const navStyle = navStyleOf(s.me);
  useEffect(() => applyNavStyle(navStyle), [navStyle]);
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const toast = useToast();
  const updateReady = useUpdateReady();
  const announced = useRef(false);
  const ops = useOps();
  // painéis da pessoa (os seus e os compartilhados com ela) logo abaixo da Visão geral e das Decisões
  const dashKey = location.pathname.startsWith('/dashboards') ? location.pathname : '';
  const myDashboards = useLoad(
    () => (s.can('reports:read') ? unwrap(s.api.GET('/api/v1/reports/dashboards')).catch(() => []) : Promise.resolve([])),
    [s.api, dashKey],
  );
  const [releasing, setReleasing] = useState(false);
  // R11-12: instalação sem relé (HIVE_REMOTE_DISABLED=true) → o menu do acesso remoto some
  const remoteOff = useLoad(
    () => (['remote:access', 'remote:approve'].some((p) => s.can(p)) ? loadRelay(s.api).then((r) => r.disabled).catch(() => false) : Promise.resolve(false)),
    [s.api],
  );

  const [picked, setPicked] = useState<string | null>(null);
  // submenu (segunda coluna do trilho): recolhido ou fixo, por pessoa neste navegador; sem escolha, recolhe em telas estreitas
  const [subCollapsed, setSubCollapsed] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(SUB_KEY);
      if (v !== null) return v === '1';
    } catch {
      /* armazenamento bloqueado: segue o padrão */
    }
    return typeof window !== 'undefined' && window.innerWidth < 1280;
  });
  const [flyout, setFlyout] = useState(false);
  // grupos do menu em árvore abertos por quem usa (o da tela atual abre sozinho)
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(GROUPS_KEY) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const pinSub = (collapsed: boolean) => {
    setSubCollapsed(collapsed);
    setFlyout(false);
    try {
      localStorage.setItem(SUB_KEY, collapsed ? '1' : '0');
    } catch {
      /* só não lembra */
    }
  };
  const navigate = useNavigate();
  useEffect(() => {
    setNavOpen(false);
    setPicked(null);
    setFlyout(false);
  }, [location.pathname]);
  // submenu flutuante fecha com Esc ou clicando fora do menu
  useEffect(() => {
    if (!flyout) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFlyout(false);
    };
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('#sidebar')) setFlyout(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [flyout]);

  // Foco no título da tela a cada navegação (leitores de tela anunciam a tela nova).
  useEffect(() => {
    const h = document.querySelector<HTMLElement>('main [data-page-title]');
    h?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (consumeUpdatedFlag()) toast(t('pwa.updated'), { kind: 'ok' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uma vez
  }, []);
  useEffect(() => {
    if (updateReady && !announced.current) {
      announced.current = true;
      toast(t('pwa.updateReady'), { kind: 'info', timeout: 60000, action: { label: t('pwa.reload'), run: applyUpdate } });
    }
  }, [updateReady, toast, t]);

  // Ctrl+K / ⌘K abre a busca global de qualquer tela (HU-005).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // menu por tela: o item aparece para quem pode ver a tela (perfil por tela ou o que as permissões antigas cobrem)
  const { data: screenCat, failed: catFailed } = useScreenCatalogState();
  // até o catálogo chegar não se sabe que telas o Autonomous assumiu: o menu (computador e celular) e a tela esperam
  const catReady = !!screenCat || catFailed;
  const allowed = (i: NavItem) => !i.perm || (Array.isArray(i.perm) ? i.perm.some((p) => s.can(p)) : s.can(i.perm));
  const visible = (l: NavItem[]) =>
    l.filter((i) => {
      if (!catReady) return false;
      // atalho com filtro (/network?layers=virt) vale pela tela da rota
      const route = i.to.split('?')[0];
      // integração com o Autonomous ligada: as telas que ele assumiu somem para todos os perfis
      if (autonomousOf(screenCat, route ?? i.to)) return false;
      const sc = screenCat?.blocks.flatMap((b) => b.screens).find((x) => x.route === route);
      if (sc) return s.can(`screen:${sc.id}:view`);
      return allowed(i);
    });
  // só para o caminho do cabeçalho antes do catálogo (o menu continua esperando por ele): pelas permissões, o caminho
  // já nasce "Monitorar / Alertas" — antes nascia "Início" e trocava, empurrando a busca (CLS de /alerts, QA 5 · Q5-6)
  const byPerm = (l: NavItem[]) => l.filter(allowed);
  // link guardado, atalho ou busca que leva a uma tela do Autonomous: a rota explica em vez de abrir a tela
  const managed = autonomousOf(screenCat, location.pathname);
  const home: NavItem = { to: '/', label: t('nav.overview'), icon: 'home', end: true };
  // com o Autonomous: Atividades Operacionais (a fila só com o que se decide no Hive)
  const decisions: NavItem = { to: '/decisions', label: t(screenCat?.autonomous?.enabled ? 'auto.ops.nav' : 'nav.decisions'), icon: 'check', badge: ops.count };
  // layout modelo 1 (20/10): espaços no trilho, cada um com o submenu curto das suas telas
  const makeSpaces = (visible: (l: NavItem[]) => NavItem[]): Space[] => ([
    {
      id: 'home',
      label: t('ws.home'),
      icon: 'home',
      items: [
        home,
        // a fila também é tela da matriz de perfis (home.decisions)
        ...visible([decisions]),
        ...[...(myDashboards.data ?? [])]
          .sort((a, b) => Number(!!b.mine) - Number(!!a.mine) || (a.name ?? '').localeCompare(b.name ?? ''))
          .slice(0, 20)
          .map((x): NavItem => ({ to: `/dashboards/${x.id}`, label: x.name ?? '', icon: x.mine ? 'chart' : 'users' })),
      ],
    },
    {
      id: 'assets',
      label: t('ws.assets'),
      icon: 'laptop',
      items: visible([
        { to: '/assets', label: t('nav.allAssets'), icon: 'laptop', perm: 'assets:read', end: true },
        { to: '/mobile', label: t('nav.mobile'), icon: 'monitor', perm: 'assets:read' },
        { to: '/mdm', label: t('nav.mdm'), icon: 'lock', perm: 'assets:read', end: true },
        { to: '/kiosks', label: t('nav.kiosks'), icon: 'monitor', perm: ['assets:read', 'config:write'] },
        { to: '/software', label: t('nav.software'), icon: 'grid', perm: 'assets:read' },
        { to: '/agents', label: t('nav.agents'), icon: 'box', perm: 'assets:read' },
        { to: '/uninstall-code', label: t('nav.uninstallCode'), icon: 'key', perm: 'action:agents.uninstallCode' },
        { to: '/assets/types', label: t('nav.assetTypes'), icon: 'layers', perm: 'assets:read' },
        { to: '/assets/lifecycle-flow', label: t('nav.lifecycleFlow'), icon: 'refresh', perm: 'assets:read' },
        { to: '/tags', label: t('nav.tags'), icon: 'tag', perm: 'assets:read' },
      ]),
    },
    {
      id: 'discovery',
      label: t('ws.discovery'),
      icon: 'search',
      // de onde vêm os ativos: estrutura da empresa, varredura da rede, virtualização, nuvem e Kubernetes
      items: visible([
        { to: '/org', label: t('nav.org'), icon: 'folder', perm: 'assets:read' },
        { to: '/discovery', label: t('nav.discovery'), icon: 'search', perm: 'assets:read' },
        { to: '/virtualization', label: t('nav.virtualization'), icon: 'box', perm: 'assets:read' },
        // R12-04: CLPs e demais equipamentos industriais, só nas faixas marcadas como OT
        { to: '/ot', label: t('nav.ot'), icon: 'grid', perm: 'assets:read' },
        { to: '/cloud', label: t('nav.cloud'), icon: 'layers', perm: 'assets:read' },
        { to: '/cloud-posture', label: t('nav.cloudPosture'), icon: 'shield', perm: 'assets:read' },
        { to: '/kubernetes', label: t('nav.kubernetes'), icon: 'grid', perm: 'assets:read' },
        { to: '/apis', label: t('nav.apis'), icon: 'grid', perm: 'assets:read' },
      ]),
    },
    {
      id: 'maps',
      label: t('ws.maps'),
      icon: 'layers',
      // todos os mapas num lugar: físico, lógico do serviço ao dado, caminho do serviço e o montador
      items: visible([
        { to: '/network', label: t('nav.network'), icon: 'layers', perm: 'assets:read' },
        // panorama de quedas: os locais no mapa do Brasil ou do mundo, com o epicentro e a linha do tempo
        { to: '/maps/geo', label: t('nav.geoMap'), icon: 'layers', perm: 'assets:read' },
        { to: '/firewalls-wifi', label: t('nav.firewallsWifi'), icon: 'shield', perm: 'assets:read' },
        { to: '/config-backups', label: t('nav.configBackups'), icon: 'layers', perm: 'assets:read' },
        { to: '/topology/logical', label: t('nav.logicalMap'), icon: 'grid', perm: 'assets:read' },
        { to: '/service-map', label: t('nav.serviceMap'), icon: 'chart', perm: 'assets:read' },
        // atalhos: os mesmos mapas já filtrados (físico com a virtualização; lógico com o cluster)
        { to: '/network?layers=virt', label: t('nav.mapsVirtualization'), icon: 'box', perm: 'assets:read' },
        { to: '/topology/logical?src=k8s', label: t('nav.mapsKubernetes'), icon: 'grid', perm: 'assets:read' },
        { to: '/ot/zone', label: t('nav.otZone'), icon: 'layers', perm: 'assets:read' },
        { to: '/maps/builder', label: t('nav.mapBuilder'), icon: 'layers', perm: 'assets:read' },
        { to: '/graph', label: t('nav.graph'), icon: 'grid', perm: 'assets:read' },
      ]),
    },
    {
      id: 'monitor',
      label: t('ws.monitor'),
      icon: 'bell',
      items: visible([
        { to: '/noc', label: t('nav.noc'), icon: 'monitor', perm: 'alerts:read' },
        { to: '/soc', label: t('nav.socWall'), icon: 'shield', perm: 'alerts:read' },
        // painel de atendimento: indicadores dos chamados do ITSM conectado (o service desk é o CITSmart)
        { to: '/service-desk', label: t('nav.serviceDesk'), icon: 'chart', perm: 'alerts:read' },
        { to: '/alerts', label: t('nav.alerts'), icon: 'bell', perm: 'alerts:read', end: true },
        { to: '/availability', label: t('nav.availability'), icon: 'chart', perm: 'alerts:read' },
        { to: '/sla-matrix', label: t('nav.slaMatrix'), icon: 'grid', perm: ['alerts:read', 'reports:read'] },
        { to: '/root-causes', label: t('nav.rootCauses'), icon: 'chart', perm: 'alerts:read' },
        // R01-15: o que saiu do comportamento normal aprendido toda noite (linha de base, pares, mudança de padrão)
        { to: '/behavior', label: t('nav.behavior'), icon: 'chart', perm: 'alerts:read' },
        { to: '/experience', label: t('nav.experience'), icon: 'monitor', perm: 'assets:read' },
        { to: '/sentiment', label: t('nav.sentiment'), icon: 'users', perm: 'assets:read' },
        { to: '/applications', label: t('nav.applications'), icon: 'layers', perm: 'assets:read' },
        // Desempenho: Apdex das aplicações e NPM (uso, perda e jitter dos links)
        { to: '/performance', label: t('nav.performance'), icon: 'chart', perm: ['assets:read', 'alerts:read'] },
        // uso real de servidores, VMs e instâncias de nuvem: está em Monitorar porque nasce das métricas dos agentes
        { to: '/rightsizing', label: t('nav.rightsizing'), icon: 'chart', perm: 'assets:read' },
        // R12-03: traps que switches, nobreaks, storages e servidores mandam ao coletor
        { to: '/snmp-traps', label: t('nav.snmpTraps'), icon: 'bell', perm: 'alerts:read' },
        { to: '/alerts/rules', label: t('nav.rules'), icon: 'rule', perm: 'alerts:read' },
        { to: '/maintenance-windows', label: t('nav.maintenanceWindows'), icon: 'pause', perm: 'alerts:read' },
        { to: '/alerts/channels', label: t('nav.channels'), icon: 'send', perm: 'alerts:read' },
        { to: '/alerts/oncall', label: t('nav.oncall'), icon: 'users', perm: 'alerts:read' },
        { to: '/itsm', label: t('nav.itsm'), icon: 'layers', perm: 'integrations:read' },
        { to: '/insights', label: t('nav.insights'), icon: 'alert', perm: 'vulnerabilities:read' },
        { to: '/ai', label: t('nav.ai'), icon: 'chart', perm: ['vulnerabilities:read', 'licenses:admin', 'audit:read', 'settings:admin'] },
      ]),
    },
    {
      id: 'security',
      label: t('ws.security'),
      icon: 'shield',
      items: visible([
        { to: '/response', label: t('nav.response'), icon: 'shield', perm: 'alerts:read' },
        { to: '/hunting', label: t('nav.hunting'), icon: 'search', perm: 'alerts:read' },
        { to: '/defender', label: t('nav.defender'), icon: 'shield', perm: 'assets:read' },
        { to: '/vulnerabilities', label: t('nav.vulns'), icon: 'shield', perm: 'vulnerabilities:read' },
        { to: '/identities', label: t('nav.identities'), icon: 'users', perm: 'users:read' },
        { to: '/access-reviews', label: t('nav.accessReviews'), icon: 'users', perm: 'users:read' },
        { to: '/saas', label: t('nav.saas'), icon: 'globe', perm: 'assets:read' },
        { to: '/attack-paths', label: t('nav.attackPaths'), icon: 'layers', perm: 'vulnerabilities:read' },
        { to: '/reputation', label: t('nav.reputation'), icon: 'globe', perm: 'vulnerabilities:read' },
        { to: '/code', label: t('nav.code'), icon: 'layers', perm: 'vulnerabilities:read' },
        { to: '/external-surface', label: t('nav.surface'), icon: 'globe', perm: 'vulnerabilities:read' },
        { to: '/ioc', label: t('nav.ioc'), icon: 'search', perm: 'assets:read' },
        { to: '/cti', label: t('nav.cti'), icon: 'shield', perm: 'vulnerabilities:read' },
        { to: '/threat-intel', label: t('nav.threatIntel'), icon: 'globe', perm: 'assets:read' },
        { to: '/app-control', label: t('nav.appControl'), icon: 'lock', perm: 'assets:read' },
        { to: '/device-control', label: t('nav.deviceControl'), icon: 'lock', perm: 'assets:read' },
        { to: '/disk-encryption', label: t('nav.diskEncryption'), icon: 'key', perm: 'config:read' },
        { to: '/integrity', label: t('nav.integrity'), icon: 'lock', perm: 'assets:read' },
        ...(remoteOff.data ? [] : [{ to: '/remote', label: t('nav.remote'), icon: 'send', perm: ['remote:access', 'remote:approve', 'audit:read'] } satisfies NavItem]),
      ]),
    },
    {
      id: 'governance',
      label: t('ws.governance'),
      icon: 'doc',
      items: visible([
        { to: '/licenses', label: t('nav.licenses'), icon: 'key', perm: ['licenses:admin', 'assets:read'] },
        { to: '/contracts', label: t('nav.contracts'), icon: 'doc', perm: ['licenses:admin', 'audit:read'] },
        { to: '/lifecycle', label: t('nav.lifecycle'), icon: 'history', perm: 'assets:read' },
        { to: '/certificates', label: t('nav.certificates'), icon: 'shield', perm: 'assets:read' },
        { to: '/compliance', label: t('nav.compliance'), icon: 'shield', perm: ['reports:read', 'config:read'] },
        { to: '/privacy', label: t('nav.privacy'), icon: 'lock', perm: 'privacy:read' },
        { to: '/reports', label: t('nav.reports'), icon: 'doc', perm: 'reports:read' },
        { to: '/dashboards', label: t('nav.dashboards'), icon: 'chart', perm: 'reports:read' },
      ]),
    },
    {
      id: 'delivery',
      label: t('ws.delivery'),
      icon: 'box',
      // o que muda o dispositivo pelo administrador, e a Loja que o usuário pede pelo agente
      items: visible([
        { to: '/store/admin', label: t('nav.storeAdmin'), icon: 'box', perm: 'store:admin' },
        { to: '/approvals', label: t('nav.approvals'), icon: 'check', perm: ['licenses:admin', 'vulnerabilities:approve', 'store:admin', 'config:write', 'settings:admin', 'privacy:approve'] },
        { to: '/patches', label: t('nav.patches'), icon: 'shield', perm: 'assets:read' },
        { to: '/scripts', label: t('nav.scripts'), icon: 'key', perm: 'config:read' },
        { to: '/signing', label: t('nav.signing'), icon: 'lock', perm: 'config:read' },
        { to: '/playbooks', label: t('nav.playbooks'), icon: 'play', perm: 'config:read' },
        { to: '/workflows', label: t('nav.workflows'), icon: 'rule', perm: 'config:read' },
        { to: '/deployments', label: t('nav.deployments'), icon: 'send', perm: ['store:admin', 'assets:read'] },
        { to: '/privileges', label: t('nav.privileges'), icon: 'lock', perm: ['assets:read', 'config:read'] },
        { to: '/store/catalog', label: t('nav.storeCatalog'), icon: 'layers', perm: 'store:admin' },
        { to: '/store/third-party', label: t('nav.thirdParty'), icon: 'box', perm: 'store:admin' },
        { to: '/config-profiles', label: t('nav.configProfiles'), icon: 'rule', perm: ['config:write', 'assets:read'] },
        { to: '/store', label: t('nav.storePortal'), icon: 'grid', perm: ['store:use', 'store:admin'], end: true },
      ]),
    },
    {
      id: 'integrations',
      label: t('ws.integrations'),
      icon: 'plug',
      items: visible([
        { to: '/integrations', label: t('nav.integrations'), icon: 'plug', perm: 'integrations:read' },
        { to: '/security-connectors', label: t('nav.securityConnectors'), icon: 'shield', perm: 'integrations:read' },
        { to: '/automation', label: t('nav.automation'), icon: 'rule', perm: 'integrations:read' },
        { to: '/directory', label: t('nav.directory'), icon: 'folder', perm: 'integrations:read' },
        { to: '/credentials', label: t('dc.tab.credentials'), icon: 'key', perm: 'assets:read' },
      ]),
    },
  ] satisfies Space[]).filter((w) => w.items.length > 0);
  const spaces = makeSpaces(visible);
  const makeBottom = (visible: (l: NavItem[]) => NavItem[]): Space[] => ([
    {
      id: 'admin',
      label: t('ws.admin'),
      icon: 'users',
      items: visible([
        { to: '/users', label: t('nav.users'), icon: 'users', perm: 'users:read' },
        { to: '/audit', label: t('nav.audit'), icon: 'history', perm: 'audit:read' },
        { to: '/config-history', label: t('nav.configHistory'), icon: 'history', perm: 'config:read' },
        { to: '/templates', label: t('nav.templates'), icon: 'grid' },
        { to: '/settings', label: t('nav.settings'), icon: 'edit', perm: 'settings:admin', end: true },
        { to: '/settings/license', label: t('nav.license'), icon: 'key', perm: 'settings:admin' },
        { to: '/settings/health', label: t('nav.health'), icon: 'shield', perm: 'settings:admin' },
      ]),
    },
    { id: 'help', label: t('ws.help'), icon: 'doc', items: [{ to: '/help', label: t('nav.help'), icon: 'doc' }] },
  ] satisfies Space[]).filter((w) => w.items.length > 0);
  const bottom = makeBottom(visible);
  const all = [...spaces, ...bottom];
  // o espaço aberto é o da tela atual (a rota mais longa que casa); escolher no trilho troca o submenu sem sair da tela
  const matches = (i: NavItem) => (i.end || i.to === '/' ? location.pathname === i.to : location.pathname === i.to || location.pathname.startsWith(`${i.to}/`));
  const spaceOf = (list: Space[]) =>
    list
      .flatMap((w) => w.items.filter((i) => matches(i) || (i.to !== '/' && location.pathname.startsWith(`${i.to}/`))).map((i) => ({ w: w.id, len: i.to.length })))
      .sort((a, b) => b.len - a.len)[0]?.w ?? 'home';
  const routeSpace = spaceOf(all);
  const current: Space = all.find((w) => w.id === (picked ?? routeSpace)) ?? all[0] ?? { id: 'home', label: t('ws.home'), icon: 'home', items: [home] };
  const branding = s.me.tenant?.branding ?? {};
  const product = typeof branding.productName === 'string' && branding.productName ? branding.productName : 'Hive';
  const logo = logoSrc(s.tenant.slug, branding.logoUrl);
  // o trilho leva o ícone quadrado (ou a inicial); o logotipo completo fica no topo
  const icon = iconSrc(s.tenant.slug, branding.iconUrl);
  // caminho do cabeçalho: "Módulo / Tela" pela rota (a tela pode acrescentar trechos com BreadcrumbSlot)
  const crumbAll = catReady ? all : [...makeSpaces(byPerm), ...makeBottom(byPerm)];
  const here = crumbAll.find((w) => w.id === (catReady ? routeSpace : spaceOf(crumbAll)));
  const hereItem = here?.items.find((i) => navActive(i, here.items, location.pathname, location.search));
  const crumbScreen = hereItem && hereItem.label !== here?.label ? hereItem.label : null;
  // "Continuar de onde parou" (Início): guarda a tela aberta com o caminho
  const recentLabel = here && hereItem ? (crumbScreen ? `${here.label} › ${crumbScreen}` : here.label) : '';
  useEffect(() => {
    if (catReady && recentLabel && location.pathname !== '/') pushRecent(location.pathname + location.search, recentLabel);
  }, [catReady, location.pathname, location.search, recentLabel]);

  // encaixes das telas: painel de contexto (na coluna, ou no topo da tela no celular, no menu em árvore e com o painel
  // recolhido), ações do cabeçalho, trechos do caminho; rodapé de status
  const wide = useWide();
  const panelInColumn = wide && !subCollapsed && navStyle === 'rail';
  const ctxRef = useSlotTarget('context');
  const actionsRef = useSlotTarget('actions');
  const crumbsRef = useSlotTarget('crumbs');
  const slots = useShellSlots();
  const status = slots?.status;
  // painel recolhido ou janela estreita: o painel da tela abre como gaveta (botão "Painel" no cabeçalho ou "Filtros (n)"
  // da barra da lista), nunca como bloco aberto no conteúdo
  const [ctxOpen, setCtxOpen] = useState(false);
  const ctxDrawer = useRef<HTMLDivElement>(null);
  useFocusTrap(ctxDrawer, ctxOpen && !panelInColumn);
  const openCtx = useCallback(() => setCtxOpen(true), []);
  const setPanel = slots?.setPanel;
  useEffect(() => {
    setPanel?.({ visible: panelInColumn, open: openCtx });
  }, [setPanel, panelInColumn, openCtx]);
  useEffect(() => () => setPanel?.(null), [setPanel]);
  useEffect(() => setCtxOpen(false), [location.pathname, panelInColumn]);
  useEffect(() => {
    if (!ctxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.scrim-modal')) setCtxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ctxOpen]);
  const hasCtx = (slots?.ctx.count ?? 0) > 0;
  // tela com árvore própria (ex.: compartimentos da Nuvem): o menu da seção começa recolhido
  const ctxReplace = (slots?.ctx.replace ?? 0) > 0;
  const approvals = visible([decisions]).length ? ops.count : 0;

  // comandos da casca no Ctrl K: abrir qualquer tela do menu, trocar o tema, mostrar/recolher o painel e procurar ativo
  const themeNow = themeOf(s.me);
  const assetWord = t('cmd.assetWord').toLowerCase();
  const shellCommands: ShellCommand[] = [
    ...all.flatMap((w) =>
      w.items.map((i): ShellCommand => ({
        id: `go:${i.to}`,
        label: t('cmd.go', { screen: i.label === w.label ? w.label : `${w.label} › ${i.label}` }),
        keywords: [w.label, i.label],
        icon: i.icon,
        run: () => navigate(i.to),
      })),
    ),
    {
      id: 'shell:theme',
      label: t('cmd.theme', { name: t(`theme.${themeNow}`) }),
      icon: themeIcon(themeNow),
      run: () => void s.setTheme(nextTheme(themeNow)).catch(() => undefined),
    },
    { id: 'shell:panel', label: t('cmd.panel'), icon: 'layers', run: () => pinSub(!subCollapsed) },
    ...(s.can('assets:read')
      ? [
          {
            id: 'shell:asset',
            label: t('cmd.assetHint'),
            hint: t('cmd.assetHint'),
            icon: 'laptop',
            parse: (q: string) => {
              const m = new RegExp(`^(?:${assetWord}|asset)\\s+(.+)$`, 'i').exec(q);
              const term = m?.[1]?.trim();
              return term ? { label: t('cmd.asset', { q: term }), run: () => navigate(`/assets?q=${encodeURIComponent(term)}`) } : null;
            },
          } satisfies ShellCommand,
          {
            // "nuvem <conta>": a lista das contas abre a conta quando só uma casa (senão fica filtrada)
            id: 'shell:cloud',
            label: t('cll.cmd.hint'),
            hint: t('cll.cmd.hint'),
            icon: 'layers',
            parse: (q: string) => {
              const m = /^(?:nuvem|cloud|nube)\s+(.+)$/i.exec(q);
              const term = m?.[1]?.trim();
              return term ? { label: t('cll.cmd.openTerm', { q: term }), run: () => navigate(`/cloud?open=${encodeURIComponent(term)}`) } : null;
            },
          } satisfies ShellCommand,
        ]
      : []),
  ];
  useCommands(shellCommands);

  const railBtn = (w: Space) => {
    const badge = w.items.reduce((n, i) => n + (i.badge ?? 0), 0);
    return (
      <button
        key={w.id}
        type="button"
        className="ws-btn"
        aria-current={current.id === w.id}
        title={w.label}
        onClick={() => {
          // no celular o submenu já aparece junto; no computador basta trocar o submenu (recolhido, ele abre flutuando)
          const only = w.items.length === 1 ? w.items[0] : undefined;
          if (subCollapsed && !only) setFlyout(!(flyout && current.id === w.id));
          setPicked(w.id);
          if (only) navigate(only.to);
        }}
      >
        <Icon name={w.icon} />
        <span className="ws-label">{w.label}</span>
        {badge ? <span className="nav-badge">{badge > 99 ? '99+' : badge}</span> : null}
      </button>
    );
  };
  // menu em árvore (padrão Elastic): cada espaço é um grupo que abre e fecha; o da tela atual sempre aberto
  const isOpen = (id: string) => id === routeSpace || openGroups.has(id);
  const toggleGroup = (id: string) => {
    const next = new Set(openGroups);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenGroups(next);
    try {
      localStorage.setItem(GROUPS_KEY, JSON.stringify([...next]));
    } catch {
      /* só não lembra */
    }
  };
  const treeGroup = (w: Space) => {
    const only = w.items.length === 1 ? w.items[0] : undefined;
    if (only) {
      return (
        <NavLink key={w.id} to={only.to} end={only.end} className="tree-head tree-link" onMouseEnter={titleIfCut} onFocus={titleIfCut}>
          <Icon name={w.icon} size="sm" />
          <span className="tree-label">{w.label}</span>
          {only.badge ? <span className="nav-badge">{only.badge > 99 ? '99+' : only.badge}</span> : null}
        </NavLink>
      );
    }
    const open = isOpen(w.id);
    const badge = w.items.reduce((n, i) => n + (i.badge ?? 0), 0);
    return (
      <div key={w.id} className={`tree-group${w.id === routeSpace ? ' here' : ''}`}>
        <button type="button" className="tree-head" aria-expanded={open} onMouseEnter={titleIfCut} onFocus={titleIfCut} onClick={() => toggleGroup(w.id)}>
          <Icon name={w.icon} size="sm" />
          <span className="tree-label">{w.label}</span>
          {!open && badge ? <span className="nav-badge">{badge > 99 ? '99+' : badge}</span> : null}
          <Icon name={open ? 'chev' : 'chevR'} size="sm" className="tree-chev" />
        </button>
        {open && (
          <div className="tree-items">
            {w.items.map((i) => (
              <NavItemLink key={i.to} item={i} siblings={w.items} />
            ))}
          </div>
        )}
      </div>
    );
  };
  const nav = (
    <>
      <nav className="tree-nav" aria-label={t('ws.label')}>
        {/* os grupos entram juntos com o catálogo (antes, os que chegavam depois empurravam os de baixo) */}
        <div className="tree-scroll">{catReady && spaces.map(treeGroup)}</div>
        <div className="tree-bottom">
          {catReady && bottom.map(treeGroup)}
          <button type="button" className="tree-head tree-collapse" onClick={() => pinSub(true)} title={t('ws.collapse')}>
            <Icon name="chevL" size="sm" />
            <span className="tree-label">{t('ws.collapse')}</span>
          </button>
        </div>
      </nav>
      <nav className="ws-rail" aria-label={t('ws.label')}>
        <NavLink to="/" className="rail-brand" aria-label={t('top.home', { product })} title={s.me.tenant?.name ?? s.tenant.name}>
          <RailBrandMark icon={icon} product={product} />
        </NavLink>
        {spaces.map(railBtn)}
        <span className="ws-sep" />
        {bottom.map(railBtn)}
        {subCollapsed && (
          <button type="button" className="icon-btn ws-expand" title={t('ws.expand')} aria-label={t('ws.expand')} onClick={() => pinSub(false)}>
            <Icon name="chevR" />
          </button>
        )}
      </nav>
      <ContextPanel
        className={flyout ? 'ws-sub flyout' : 'ws-sub'}
        title={current.label}
        label={current.label}
        collapsed={subCollapsed}
        onToggle={() => pinSub(!subCollapsed)}
        sections={[
          {
            id: 'menu',
            title: t('ui.ctx.menu'),
            collapsible: true,
            defaultOpen: !(ctxReplace && current.id === routeSpace),
            rememberKey: ctxReplace && current.id === routeSpace ? 'ctx.menu.withTree' : 'ctx.menu',
            // os itens entram juntos com o catálogo: a "Decisões" (e as telas por perfil) entrando depois empurrava os
            // painéis da pessoa para baixo (CLS do Início, QA 5 · Q5-4)
            items: (catReady ? current.items : []).map((i) => ({
              id: i.to,
              label: i.label,
              to: i.to,
              title: i.label,
              active: navActive(i, current.items, location.pathname, location.search),
              count: i.badge ? (i.badge > 99 ? '99+' : i.badge) : null,
            })),
          },
        ]}
      >
        {/* o que a tela põe no painel (visões, filtros) só fica na coluna quando ela está aberta no computador */}
        {panelInColumn && current.id === routeSpace && <div className="ctx-slot" ref={ctxRef} />}
      </ContextPanel>
    </>
  );
  // celular: as abas de baixo levam aos espaços mais usados; "Mais" abre o trilho completo
  const tabs = ['home', 'assets', 'monitor', 'security'].map((id) => all.find((w) => w.id === id)).filter((w): w is Space => !!w);
  const bottomNav = (
    <nav className="ws-tabbar" aria-label={t('ws.label')}>
      {tabs.map((w) => (
        <button
          key={w.id}
          type="button"
          className="ws-btn"
          aria-current={routeSpace === w.id && !navOpen}
          onClick={() => {
            setPicked(w.id);
            const first = w.items[0];
            if (first) navigate(first.to);
          }}
        >
          <Icon name={w.icon} />
          <span className="ws-label">{w.label}</span>
        </button>
      ))}
      <button type="button" className="ws-btn" aria-current={navOpen} aria-expanded={navOpen} aria-controls="sidebar" onClick={() => setNavOpen((v) => !v)}>
        <Icon name="menu" />
        <span className="ws-label">{t('ws.more')}</span>
      </button>
    </nav>
  );

  return (
    <>
      <AppShell
        navLabel={t('app.navLabel')}
        navOpen={navOpen}
        onCloseNav={() => setNavOpen(false)}
        topbar={
          <>
            <button
              type="button"
              className="icon-btn menu-toggle"
              aria-label={navOpen ? t('ui.closeMenu') : t('ui.openMenu')}
              aria-expanded={navOpen}
              aria-controls="sidebar"
              onClick={() => setNavOpen((v) => !v)}
            >
              <Icon name="menu" />
            </button>
            <NavLink to="/" className="brand tb-brand" aria-label={t('top.home', { product })}>
              <Brand productName={product} logoUrl={logo} compact />
            </NavLink>
            <nav className="crumbs" aria-label={t('ui.crumbs')}>
              <ol>
                {here && (
                  <li>
                    {crumbScreen && here.items[0] ? <Link to={here.items[0].to}>{here.label}</Link> : <span aria-current={crumbScreen ? undefined : 'page'}>{here.label}</span>}
                  </li>
                )}
                {crumbScreen && (
                  <li>
                    {(slots?.crumbs ?? 0) > 0 && hereItem ? <Link to={hereItem.to}>{crumbScreen}</Link> : <span aria-current="page">{crumbScreen}</span>}
                  </li>
                )}
                <li className="crumb-extra" ref={crumbsRef} />
              </ol>
            </nav>
            {!panelInColumn && hasCtx && (
              <button type="button" className="btn btn-sm tb-ctx" aria-expanded={ctxOpen} aria-controls="ctx-drawer" onClick={() => setCtxOpen((v) => !v)}>
                <Icon name="rule" size="sm" /> <span className="lbl">{t('ui.ctx.openPanel')}</span>
              </button>
            )}
            <button className="tb-search" type="button" aria-label={t('top.searchLabel')} onClick={() => setSearchOpen(true)}>
              <Icon name="search" />
              <span className="lbl">{t('top.search')}</span>
              <kbd>Ctrl K</kbd>
            </button>
            <div className="tb-actions" ref={actionsRef} />
            <div className="tb-right">
              {catReady ? (
                !autonomousOf(screenCat, '/insights') && <InsightsButton />
              ) : s.can('vulnerabilities:read') ? (
                // lugar do botão guardado até o catálogo chegar: os botões da barra não andam quando ele aparece
                <span className="icon-btn" aria-hidden="true" style={{ visibility: 'hidden' }}>
                  <Icon name="alert" />
                </span>
              ) : null}
              <StartGuideButton />
              <HelpButton />
              <ThemeButton />
              <UserMenu />
            </div>
          </>
        }
        nav={nav}
        layout="rail"
        subCollapsed={subCollapsed}
        bottomNav={bottomNav}
        statusBar={
          <StatusBar
            items={status?.items.length ? status.items : [{ id: 'shortcuts', label: t('foot.shortcuts') }]}
            updatedAt={status?.updatedAt}
            what={status?.what}
            approvals={approvals}
            approvalsTo="/decisions"
          />
        }
      >
        {ops.brake?.active && (
          <div className="brake-banner" role="alert">
            <b>{t('brake.banner')}</b>
            <span>{t('brake.bannerBy', { who: ops.brake.pulledBy ?? '', reason: ops.brake.reason ?? '' })}</span>
            {canBrake(s.can) && (
              <button type="button" className="btn btn-sm" onClick={() => setReleasing(true)}>
                {t('brake.release')}
              </button>
            )}
          </div>
        )}
        {/* uma tela que quebra ao desenhar mostra a caixa de erro amigável em vez de ficar em branco; trocar de tela limpa */}
        {/* a tela só abre com o catálogo na mão: antes dele não se sabe se ela é do Autonomous (link guardado, atalho do
            PWA, clique numa notificação abriam a tela oculta por um instante, com as chamadas de API dela). Sem catálogo
            (falha de rede), abre como antes: a trava de rota do núcleo continua valendo */}
        <ErrorBoundary resetKey={location.pathname}>{managed ? <AutonomousManaged screen={managed} /> : catReady ? children : <Loading />}</ErrorBoundary>
      </AppShell>
      {!panelInColumn && (
        <>
          {ctxOpen && <div className="ctx-drawer-scrim" onClick={() => setCtxOpen(false)} aria-hidden="true" />}
          {/* sempre montada (escondida): é o alvo do encaixe do painel enquanto ele não está na coluna */}
          <div ref={ctxDrawer} id="ctx-drawer" className="ctx-drawer" role="dialog" aria-modal="true" aria-label={t('ui.ctx.panel')} tabIndex={-1} hidden={!ctxOpen}>
            <ContextPanel
              title={here?.label ?? current.label}
              label={t('ui.ctx.panel')}
              sections={
                here
                  ? [
                      {
                        id: 'menu',
                        title: t('ui.ctx.menu'),
                        collapsible: true,
                        defaultOpen: !ctxReplace,
                        rememberKey: ctxReplace ? 'ctx.menu.withTree' : 'ctx.menu',
                        items: here.items.map((i) => ({ id: i.to, label: i.label, to: i.to, title: i.label, active: navActive(i, here.items, location.pathname, location.search) })),
                      },
                    ]
                  : []
              }
            >
              <button type="button" className="btn btn-sm ctx-drawer-close" onClick={() => setCtxOpen(false)}>
                {t('ui.ctx.closePanel')}
              </button>
              <div className="ctx-slot" ref={ctxRef} />
            </ContextPanel>
          </div>
        </>
      )}
      {releasing && (
        <BrakeModal
          active
          onClose={(done) => {
            setReleasing(false);
            if (done) opsChanged();
          }}
        />
      )}
      {searchOpen && <SearchPalette onClose={() => setSearchOpen(false)} />}
      <AskDrawer />
      <SessionExpired />
      <StepUpDialog />
    </>
  );
}

/** Põe o nome inteiro na dica só quando o texto foi cortado com reticências (vale para mouse e foco pelo teclado). */
function titleIfCut(e: { currentTarget: HTMLElement }) {
  const el = e.currentTarget;
  const label = el.querySelector<HTMLElement>('.nav-label, .tree-label') ?? el;
  if (label.scrollWidth > label.clientWidth + 1) el.title = label.textContent ?? '';
  else el.removeAttribute('title');
}

/**
 * Atalhos com filtro na URL (ex.: /topology/logical?src=k8s) apontam para a mesma tela do item sem filtro: o item
 * ativo é o que casa caminho E filtro, e o item sem filtro deixa de marcar quando um irmão com filtro casa — antes os
 * dois ficavam marcados ao mesmo tempo.
 */
function navActive(item: NavItem, siblings: NavItem[], pathname: string, search: string) {
  const split = (to: string) => {
    const [path, qs = ''] = to.split('?');
    return { path, params: [...new URLSearchParams(qs).entries()] };
  };
  const here = new URLSearchParams(search);
  const hit = (to: string, end?: boolean) => {
    const { path, params } = split(to);
    const onPath = end || path === '/' ? pathname === path : pathname === path || pathname.startsWith(`${path}/`);
    // src=k8s casa também src=k8s:<cluster> (o atalho abre o primeiro cluster e a tela troca o filtro)
    return onPath && params.every(([k, v]) => here.get(k) === v || (here.get(k) ?? '').startsWith(`${v}:`));
  };
  if (!hit(item.to, item.end)) return false;
  if (item.to.includes('?')) return true;
  return !siblings.some((x) => x !== item && x.to.includes('?') && split(x.to).path === split(item.to).path && hit(x.to, x.end));
}

function NavItemLink({ item, siblings }: { item: NavItem; siblings: NavItem[] }) {
  const location = useLocation();
  const active = navActive(item, siblings, location.pathname, location.search);
  return (
    <Link
      to={item.to}
      className={`nav-link${active ? ' active' : ''}`}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={titleIfCut}
      onFocus={titleIfCut}
    >
      <Icon name={item.icon} size="sm" />
      <span className="nav-label">{item.label}</span>
      {item.badge ? (
        <span className="nav-badge" aria-label={String(item.badge)}>
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </Link>
  );
}

function ThemeButton() {
  const t = useT();
  const s = useSession();
  const toast = useToast();
  const cur = themeOf(s.me);
  const name = t(`theme.${cur}`);
  const icon: IconName = themeIcon(cur);
  return (
    <button
      type="button"
      className="icon-btn tb-hide-sm"
      aria-label={t('theme.button', { name })}
      title={t('theme.button', { name })}
      onClick={async () => {
        const n = nextTheme(cur);
        await s.setTheme(n).catch(() => undefined);
        toast(t('theme.changed', { name: t(`theme.${n}`) }), { kind: 'info' });
      }}
    >
      <Icon name={icon} />
    </button>
  );
}

function initials(name: string) {
  return (
    name
      .split(/[\s.@]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0]?.toUpperCase())
      .join('') || '?'
  );
}

function UserMenu() {
  const t = useT();
  const { has, lang } = useI18nP();
  const s = useSession();
  const toast = useToast();
  const install = useInstallPrompt();
  const navigate = useNavigate();
  const [confirm, confirmNode] = useConfirm();
  // integração com o Autonomous: o Hive não manda push de alerta (quem avisa é ele), então o item some
  const autonomous = !!useScreenCatalog()?.autonomous?.enabled;
  const me = s.me;
  const display = me.name || me.email || '';
  const active = profileName(t, has, me.activeProfile);
  const cur = themeOf(me);
  return (
    <>
      <MenuButton
        className="user-btn"
        label={t('menu.user')}
        menuLabel={t('menu.user')}
        button={
          <>
            <span className="avatar" aria-hidden="true">
              {initials(display)}
            </span>
            <span className="user-meta">
              <b>{display}</b>
              <span>{active}</span>
            </span>
            <Icon name="chev" size="sm" />
          </>
        }
      >
        {(close) => (
          <>
            <div className="pop-head">
              <b>{display}</b>
              <span className="muted small">{me.email}</span>
              <span className="small">
                {t('menu.activeProfile')}: <b>{active}</b>
              </span>
            </div>
            <div className="pop-sep" />
            <div style={{ padding: '4px 10px 8px' }} className="lang-field">
              <span className="small strong" aria-hidden="true">
                {t('lang.label')}
              </span>
              <LangFlags label={t('lang.label')} value={lang} onChange={(l) => void s.setLang(l).catch(() => undefined)} />
            </div>
            <div className="pop-sep" />
            <button
              type="button"
              className="pop-item"
              role="menuitem"
              onClick={async () => {
                const n = nextTheme(cur);
                await s.setTheme(n).catch(() => undefined);
                toast(t('theme.changed', { name: t(`theme.${n}`) }), { kind: 'info' });
              }}
            >
              <Icon name={themeIcon(cur)} size="sm" />
              {t('menu.theme', { name: t(`theme.${cur}`) })}
            </button>
            <button
              type="button"
              className="pop-item"
              role="menuitem"
              onClick={async () => {
                const n = navStyleOf(me) === 'tree' ? 'rail' : 'tree';
                await s.setNavStyle(n).catch(() => undefined);
                toast(t('menu.navChanged', { name: t(`menu.nav.${n}`) }), { kind: 'info' });
              }}
            >
              <Icon name="grid" size="sm" />
              {t('menu.navStyle', { name: t(`menu.nav.${navStyleOf(me)}`) })}
            </button>
            {(me.profiles ?? []).length > 1 && (
              <>
                <div className="pop-sep" />
                <span className="small strong" style={{ padding: '4px 10px', display: 'block' }}>
                  {t('menu.switchProfile')}
                </span>
                {(me.profiles ?? []).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="pop-item"
                    role="menuitemradio"
                    aria-checked={p.id === me.activeProfile?.id}
                    onClick={async () => {
                      close();
                      if (!p.id || p.id === me.activeProfile?.id) return;
                      try {
                        await s.switchProfile(p.id);
                        toast(t('menu.switchedTo', { name: profileName(t, has, p) }), { kind: 'ok' });
                      } catch {
                        toast(t('menu.switchFailed'), { kind: 'crit' });
                      }
                    }}
                  >
                    <Icon name={p.id === me.activeProfile?.id ? 'check' : 'user'} size="sm" />
                    {profileName(t, has, p)}
                  </button>
                ))}
              </>
            )}
            <div className="pop-sep" />
            {me.kind === 'user' && (
              <button
                type="button"
                className="pop-item"
                role="menuitem"
                onClick={() => {
                  close();
                  navigate('/me/security');
                }}
              >
                <Icon name="lock" size="sm" />
                {t('menu.security')}
              </button>
            )}
            {s.can('alerts:read') && !autonomous && (
              <button
                type="button"
                className="pop-item"
                role="menuitem"
                onClick={() => {
                  close();
                  navigate('/me/notifications');
                }}
              >
                <Icon name="bell" size="sm" />
                {t('menu.notifications')}
              </button>
            )}
            {install.available && (
              <button
                type="button"
                className="pop-item"
                role="menuitem"
                onClick={async () => {
                  close();
                  await install.install();
                }}
              >
                <Icon name="download" size="sm" />
                {t('menu.install')}
              </button>
            )}
            <button
              type="button"
              className="pop-item"
              role="menuitem"
              onClick={async () => {
                close();
                const ok = await confirm({ title: t('menu.signOutTitle'), text: t('menu.signOutText'), ok: t('menu.signOut') });
                if (ok) await s.signOut();
              }}
            >
              <Icon name="logout" size="sm" />
              {t('menu.signOut')}
            </button>
          </>
        )}
      </MenuButton>
      {confirmNode}
    </>
  );
}

/** Sessão caiu (token vencido, revogado ou perfil que pede MFA): pede login uma vez só (HU-001). */
function SessionExpired() {
  const t = useT();
  const s = useSession();
  const location = useLocation();
  if (!s.expired) return null;
  const mfa = s.expired.code === 'mfa-required';
  const again = () => void s.auth.signIn(location.pathname + location.search, { forceLogin: mfa, lang: s.me.lang ?? undefined });
  return (
    <Modal
      title={mfa ? t('problem.mfa-required.what') : t('session.expiredTitle')}
      size="sm"
      onClose={() => s.clearExpired()}
      foot={
        <button type="button" className="btn btn-primary" onClick={again}>
          {t('session.signInAgain')}
        </button>
      }
    >
      <p>{mfa ? t('problem.mfa-required.why') : t('session.expiredText')}</p>
      {mfa && <p className="small muted">{t('problem.mfa-required.fix')}</p>}
    </Modal>
  );
}

type SearchItem = SearchHit;

/**
 * Busca e comando (T-04, Ctrl K): primeiro os comandos que casam com o texto (os da tela aberta, depois abrir telas,
 * tema, painel e "ativo <nome>"), depois os resultados da busca agrupados por tipo. Setas escolhem, Enter executa.
 */
export function SearchPalette({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { lang, has } = useI18nP();
  const s = useSession();
  const navigate = useNavigate();
  const cat = useScreenCatalog();
  const slots = useShellSlots();
  const [q, setQ] = useState('');
  const [raw, setGroups] = useState<Array<[string, SearchItem[]]>>([]);
  // integração com o Autonomous: resultado que abriria uma tela dele não aparece (o núcleo já tira; isto cobre o resto)
  const groups = raw.map(([g, items]): [string, SearchItem[]] => [g, items.filter((it) => !autonomousOf(cat, searchRoute(it)))]).filter(([, items]) => items.length > 0);
  const [sel, setSel] = useState(0);
  const [took, setTook] = useState<number | null>(null);
  useEffect(() => {
    if (!q.trim()) {
      setGroups([]);
      return;
    }
    let alive = true;
    const h = window.setTimeout(async () => {
      try {
        const r = (await unwrap(s.api.GET('/api/v1/search', { params: { query: { q, lang } } }))) as unknown as {
          groups?: Record<string, SearchItem[]>;
          tookMs?: number;
        };
        if (!alive) return;
        setGroups(Object.entries(r.groups ?? {}));
        setTook(r.tookMs ?? null);
        setSel(0);
      } catch {
        if (alive) setGroups([]);
      }
    }, 200);
    return () => {
      alive = false;
      window.clearTimeout(h);
    };
  }, [q, lang, s.api]);
  // comandos: sem texto, só os da tela e os da casca (abrir tela só aparece digitando); com texto, até 8
  const hits = matchCommands(slots?.commands ?? [], q);
  const cmds = (q.trim() ? hits : hits.filter((c) => !c.id.startsWith('go:'))).slice(0, 8);
  const flat = groups.flatMap(([, items]) => items);
  const total = cmds.length + flat.length;
  const open = useCallback(
    (it: SearchItem | undefined) => {
      if (!it) return;
      onClose();
      navigate(searchRoute(it));
    },
    [navigate, onClose],
  );
  const pick = (i: number) => {
    const c = cmds[i];
    if (c) {
      onClose();
      void c.run();
      return;
    }
    open(flat[i - cmds.length]);
  };
  let n = cmds.length - 1;
  return (
    <Modal title={t('search.title')} onClose={onClose} initialFocus="#gsearch">
      <div className="field">
        <label htmlFor="gsearch" className="sr-only">
          {t('search.title')}
        </label>
        <input
          id="gsearch"
          type="search"
          value={q}
          role="combobox"
          aria-expanded={total > 0}
          aria-controls="gsearch-res"
          aria-activedescendant={total ? `gs-${Math.min(sel, total - 1)}` : undefined}
          placeholder={t('top.search')}
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((x) => Math.min(total - 1, x + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((x) => Math.max(0, x - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              pick(Math.min(sel, total - 1));
            }
          }}
        />
      </div>
      <div id="gsearch-res" role="listbox" aria-label={t('search.results')} className="stack-sm">
        {cmds.length > 0 && (
          <div role="group" aria-label={t('ui.cmd.group')}>
            <div className="cmdk-group">{t('ui.cmd.group')}</div>
            {cmds.map((c, idx) => (
              <div
                key={`cmd-${c.id}`}
                id={`gs-${idx}`}
                role="option"
                aria-selected={idx === sel}
                className="cmdk-item"
                tabIndex={-1}
                onMouseEnter={() => setSel(idx)}
                onClick={() => pick(idx)}
                onKeyDown={() => undefined}
              >
                <span className="ic" aria-hidden="true">
                  <Icon name={c.icon ?? 'play'} size="sm" />
                </span>
                <span className="min0">
                  <span className="tt" style={{ display: 'block' }}>
                    {c.label}
                  </span>
                  {c.hint && c.hint !== c.label && <span className="sub mono">{c.hint}</span>}
                </span>
                <Icon name="chevR" size="sm" />
              </div>
            ))}
          </div>
        )}
        {q.trim() && !total && <p className="small muted">{t('search.none')}</p>}
        {groups.map(([g, items]) => (
          <div key={g} role="group" aria-label={has(`search.group.${g}`) ? t(`search.group.${g}` as never) : g}>
            <div className="cmdk-group">{has(`search.group.${g}`) ? t(`search.group.${g}` as never) : g}</div>
            {items.map((it) => {
              n++;
              const idx = n;
              return (
                <div
                  key={`${g}-${it.id}-${idx}`}
                  id={`gs-${idx}`}
                  role="option"
                  aria-selected={idx === sel}
                  className="cmdk-item"
                  tabIndex={-1}
                  onMouseEnter={() => setSel(idx)}
                  onClick={() => open(it)}
                  onKeyDown={() => undefined}
                >
                  <span className="ic" aria-hidden="true">
                    <Icon name={it.type === 'person' ? 'user' : it.type === 'screen' ? 'grid' : it.type === 'asset' ? 'laptop' : 'search'} size="sm" />
                  </span>
                  <span className="min0">
                    <span className="tt" style={{ display: 'block' }}>
                      {it.title}
                    </span>
                    {it.subtitle && <span className="sub">{it.subtitle}</span>}
                  </span>
                  <Icon name="chevR" size="sm" />
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {took != null && flat.length > 0 && <span className="xs muted">{t('search.took', { ms: took })}</span>}
    </Modal>
  );
}
